import { localized } from "@msuite/ui/localization";
import { createColumnHelper, createMeta, sizeColumns, weightColumns } from "@msuite/ui/table";
import { Tag } from "@msuite/ui/tag";
import { formatNumber } from "@msuite/utils/numbers";
import {
	getArticleQuantityStateColor,
	getArticleQuantityStateTranslationKey,
	getLoadCarrierQuantityState,
} from "@msuite/utils/stock";
import type { LoadCarrierListItem } from "@msuite/utils/types";
import { NoteText } from "@/components/notes";

const columnHelper = createColumnHelper<LoadCarrierListItem>();

export const columns = [
	columnHelper.accessor("name", {
		id: "name",
		header: localized("general.name"),
		cell: (cell) => cell.getValue(),
	}),
	columnHelper.accessor("description", {
		id: "description",
		header: localized("general.description"),
		cell: (cell) => <NoteText note={cell.getValue()} />,
		size: 350,
	}),
	// The balance carries the tag, because a carrier's thresholds are thresholds on the yard: the
	// reservation beside it counts what an unfinished round has *promised*, and a round adds to that
	// figure as the picker fills it in. The orderable column says what a new order may still ask
	// for, which is the number that differs from the balance and the reason the two sit side by side.
	columnHelper.accessor("stockBalance", {
		id: "stockBalance",
		header: localized("stock.stock_balance"),
		cell: (cell) => {
			const state = getLoadCarrierQuantityState(cell.row.original);
			return (
				<div className="w-full h-stack justify-between pr-2">
					<p>{localized(getArticleQuantityStateTranslationKey(state))}</p>
					<Tag
						direction="row-reverse"
						color={getArticleQuantityStateColor(state)}
						variant="plain"
					>
						{formatNumber(cell.getValue(), "integer", 0)}
					</Tag>
				</div>
			);
		},
	}),
	columnHelper.accessor("reservedQuantity", {
		id: "reservedQuantity",
		header: localized("stock.reserved_quantity"),
		cell: (cell) => (
			<div className="w-full h-stack justify-end pr-2">
				<Tag
					direction="row-reverse"
					color="blue"
					variant="plain"
				>
					{formatNumber(cell.getValue(), "integer", 0)}
				</Tag>
			</div>
		),
	}),
	columnHelper.accessor("availableQuantity", {
		id: "availableQuantity",
		header: localized("stock.orderable_quantity"),
		cell: (cell) => (
			<div className="w-full h-stack justify-end pr-2">
				<Tag
					direction="row-reverse"
					color="slate"
					variant="plain"
				>
					{formatNumber(cell.getValue(), "integer", 0)}
				</Tag>
			</div>
		),
	}),
	columnHelper.accessor("warningStockBalance", {
		id: "warningStockBalance",
		header: localized("stock.warning_stock_balance"),
		cell: (cell) => (
			<Tag
				direction="row-reverse"
				color="amber"
				variant="plain"
			>
				{formatNumber(cell.getValue(), "integer")}
			</Tag>
		),
		meta: createMeta({ isNumeric: true }),
	}),
	columnHelper.accessor("minimumStockBalance", {
		id: "minimumStockBalance",
		header: localized("stock.minimum_stock_balance"),
		cell: (cell) => (
			<Tag
				direction="row-reverse"
				color="red"
				variant="plain"
			>
				{formatNumber(cell.getValue(), "integer")}
			</Tag>
		),
		meta: createMeta({ isNumeric: true }),
	}),
	...sizeColumns<LoadCarrierListItem>(),
	...weightColumns<LoadCarrierListItem>(),
];
